import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useVisibleRefresh } from "@/hooks/useVisibleRefresh";
import { toast } from "sonner";
import { useDoxaConfirm } from "@/components/doxa/DoxaConfirmDialog";
import {
  Archive,
  BarChart3,
  BookOpenCheck,
  CheckCircle2,
  CircleCheck,
  Clock3,
  Copy,
  FilePenLine,
  FileText,
  ListChecks,
  Loader2,
  Lock,
  MoreHorizontal,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Send,
  Square,
  Target,
  Timer,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { useBusyKeys } from "@/hooks/useBusyKeys";
import { usePermissions } from "@/contexts/PermissionContext";
import { fmtDateTime, norm } from "@/components/RelationSelects";
import DoxaSelect from "@/components/doxa/DoxaSelect";
import { EmptyState, PageHeader, PageLoading, SectionCard } from "@/components/doxa/AdminSurface";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const STATUS_OPTS = [
  { value: "active", label: "Ativas" },
  { value: "closed", label: "Encerradas" },
  { value: "archived", label: "Arquivadas" },
  { value: "all", label: "Todas" },
];

const STATUS_LABEL = {
  DRAFT: "Rascunho",
  PUBLISHED: "Publicada",
  CLOSED: "Encerrada",
  ARCHIVED: "Arquivada",
};

const QTYPE_LABEL = {
  MULTIPLE_CHOICE: "Múltipla escolha",
  TRUE_FALSE: "Verdadeiro/Falso",
  ESSAY: "Dissertativa",
};

const STATUS_META = {
  DRAFT: { icon: FilePenLine, className: "border-[#E7DFD3] bg-[#F6F3EE] text-[#5F574D]" },
  PUBLISHED: { icon: Send, className: "border-[#CFE8D8] bg-[#EEF8F2] text-[#2F7A45]" },
  CLOSED: { icon: CircleCheck, className: "border-[#F3D9A7] bg-[#FFF7E8] text-[#9A6518]" },
  ARCHIVED: { icon: Archive, className: "border-[#E7DFD3] bg-[#F3EFE7] text-[#7B7369]" },
};

const inputCls = "w-full min-h-10 px-3 py-2 bg-white border border-[#E0D9CD] rounded-xl text-sm text-[#1F1F1F] focus:outline-none focus:border-[#F75A1B] focus:ring-2 focus:ring-[#F75A1B]/15";

function toLocalInput(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function StatusChip({ status }) {
  const meta = STATUS_META[status] || STATUS_META.DRAFT;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[11px] font-semibold ${meta.className}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function MetaItem({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-[#6B655E]">
      <Icon className="h-3.5 w-3.5 shrink-0 text-[#A59C90]" aria-hidden="true" />
      <span>{children}</span>
    </span>
  );
}

function ModalShell({ children, onClose, testId, maxWidth = "max-w-2xl" }) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-3 sm:p-4" onClick={onClose}>
      <div
        className={`max-h-[92vh] w-full ${maxWidth} overflow-y-auto rounded-[24px] border border-[#E0D9CD] bg-[#FCFAF7] shadow-[0_24px_80px_rgba(45,31,20,.22)]`}
        onClick={(event) => event.stopPropagation()}
        data-testid={testId}
      >
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ eyebrow, title, description, onClose, trailing }) {
  return (
    <header className="sticky top-0 z-10 border-b border-[#EBE5DB] bg-white/95 px-5 py-4 backdrop-blur sm:px-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          {eyebrow && <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#F75A1B]">{eyebrow}</p>}
          <div className="mt-0.5 flex flex-wrap items-center gap-2">
            <h2 className="font-display text-xl font-bold sm:text-2xl">{title}</h2>
            {trailing}
          </div>
          {description && <p className="mt-1 max-w-xl text-xs leading-relaxed text-[#6B655E] sm:text-sm">{description}</p>}
        </div>
        <button type="button" onClick={onClose} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-[#6B655E] transition hover:bg-[#FFF0E9] hover:text-[#D94A12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/25" aria-label="Fechar">
          <X className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
}

function EvaluationMenu({ evaluation, can, isBusy, run, onEdit, onResults, onPublish, onDraft, onClone, onCloseEvaluation, onArchive }) {
  const canEditView = can("eval.edit") || can("eval.view_results") || can("eval.lifecycle") || can("eval.create");
  if (!canEditView) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-transparent text-[#8A8378] transition hover:border-[#F75A1B]/20 hover:bg-[#FFF0E9] hover:text-[#F75A1B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/25 data-[state=open]:border-[#F75A1B]/20 data-[state=open]:bg-[#FFF0E9] data-[state=open]:text-[#F75A1B]"
          aria-label={`Ações da avaliação ${evaluation.title}`}
          data-testid={`evaluation-row-menu-${evaluation.id}`}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={7} className="w-64 rounded-xl border-[#E0D9CD] bg-white p-1.5 shadow-[0_14px_40px_rgba(45,31,20,.14)]">
        <DropdownMenuLabel className="px-2.5 pb-1 pt-2">
          <span className="block text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A59C90]">Ações da avaliação</span>
          <span className="mt-0.5 block truncate text-xs font-semibold normal-case tracking-normal text-[#4A4640]">{evaluation.title}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#EBE5DB]" />

        <DropdownMenuItem onSelect={onEdit} className="cursor-pointer rounded-lg px-2.5 py-2.5 focus:bg-[#FFF0E9] focus:text-[#D94A12]" data-testid={`menu-edit-evaluation-${evaluation.id}`}>
          <Pencil className="h-4 w-4" /> {evaluation.status === "DRAFT" && can("eval.edit") ? "Editar avaliação" : "Ver avaliação"}
        </DropdownMenuItem>

        {evaluation.status !== "DRAFT" && can("eval.view_results") && (
          <DropdownMenuItem onSelect={onResults} className="cursor-pointer rounded-lg px-2.5 py-2.5 focus:bg-[#FFF0E9] focus:text-[#D94A12]" data-testid={`results-evaluation-${evaluation.id}`}>
            <BarChart3 className="h-4 w-4" /> Resultados
          </DropdownMenuItem>
        )}

        {can("eval.create") && (
          <DropdownMenuItem
            disabled={isBusy(`clone:${evaluation.id}`)}
            onSelect={() => run(`clone:${evaluation.id}`, onClone)}
            className="cursor-pointer rounded-lg px-2.5 py-2.5 focus:bg-[#FFF0E9] focus:text-[#D94A12]"
            data-testid={`clone-evaluation-${evaluation.id}`}
          >
            {isBusy(`clone:${evaluation.id}`) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />} Clonar avaliação
          </DropdownMenuItem>
        )}

        {can("eval.lifecycle") && <DropdownMenuSeparator className="bg-[#EBE5DB]" />}

        {evaluation.status === "DRAFT" && can("eval.lifecycle") && (
          <DropdownMenuItem
            disabled={isBusy(`publish:${evaluation.id}`)}
            onSelect={() => run(`publish:${evaluation.id}`, onPublish)}
            className="cursor-pointer rounded-lg px-2.5 py-2.5 text-[#2F7A45] focus:bg-[#EEF8F2] focus:text-[#2F7A45]"
            data-testid={`publish-evaluation-${evaluation.id}`}
          >
            {isBusy(`publish:${evaluation.id}`) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Publicar
          </DropdownMenuItem>
        )}

        {evaluation.status === "PUBLISHED" && can("eval.lifecycle") && (
          <DropdownMenuItem
            disabled={evaluation.has_answers || isBusy(`draft:${evaluation.id}`)}
            onSelect={() => run(`draft:${evaluation.id}`, onDraft)}
            className="cursor-pointer rounded-lg px-2.5 py-2.5 focus:bg-[#FFF0E9] focus:text-[#D94A12] disabled:opacity-45"
            data-testid={`draft-evaluation-${evaluation.id}`}
          >
            {isBusy(`draft:${evaluation.id}`) ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Voltar para rascunho
          </DropdownMenuItem>
        )}

        {evaluation.status === "PUBLISHED" && can("eval.lifecycle") && (
          <DropdownMenuItem
            disabled={isBusy(`close:${evaluation.id}`)}
            onSelect={() => run(`close:${evaluation.id}`, onCloseEvaluation)}
            className="cursor-pointer rounded-lg px-2.5 py-2.5 text-[#9A6518] focus:bg-[#FFF7E8] focus:text-[#9A6518]"
            data-testid={`close-evaluation-${evaluation.id}`}
          >
            {isBusy(`close:${evaluation.id}`) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4" />} Encerrar avaliação
          </DropdownMenuItem>
        )}

        {evaluation.status !== "ARCHIVED" && can("eval.lifecycle") && (
          <>
            <DropdownMenuSeparator className="bg-[#EBE5DB]" />
            <DropdownMenuItem
              disabled={isBusy(`archive:${evaluation.id}`)}
              onSelect={() => run(`archive:${evaluation.id}`, onArchive)}
              className="cursor-pointer rounded-lg px-2.5 py-2.5 text-[#8A5A16] focus:bg-[#FFF7E8] focus:text-[#8A5A16]"
              data-testid={`archive-evaluation-${evaluation.id}`}
            >
              {isBusy(`archive:${evaluation.id}`) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />} Arquivar
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Evaluations() {
  const confirmAction = useDoxaConfirm();
  const { can } = usePermissions();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("active");
  const [q, setQ] = useState("");
  const [wizard, setWizard] = useState(false);
  const [editing, setEditing] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isBusy, run } = useBusyKeys();

  const load = () => {
    setLoading(true);
    const apiStatus = status === "closed" ? "active" : status;
    return api.get("/evaluations", { params: { status_filter: apiStatus } })
      .then(({ data }) => setItems(data))
      .catch((error) => toast.error(formatApiError(error)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { void load(); }, [status]);
  useVisibleRefresh(() => load(), { intervalMs: 0 });

  const scopedItems = items.filter((evaluation) => {
    if (status === "active") return evaluation.status === "DRAFT" || evaluation.status === "PUBLISHED";
    if (status === "closed") return evaluation.status === "CLOSED";
    return true;
  });

  const filtered = scopedItems.filter((evaluation) => (
    !q || norm([evaluation.title, evaluation.audience_label].join(" ")).includes(norm(q))
  ));

  const publish = async (evaluation) => {
    try {
      await api.post(`/evaluations/${evaluation.id}/publish`);
      toast.success("Avaliação publicada!");
      await load();
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const returnToDraft = (evaluation) => {
    if (evaluation.has_answers) return toast.error("Não é possível voltar para rascunho porque já existe uma tentativa iniciada.");
    return confirmAction({
      title: "Voltar avaliação para rascunho?",
      description: <><strong className="font-semibold text-[#1F1F1F]">{evaluation.title}</strong> deixará de ficar disponível aos alunos até ser publicada novamente.</>,
      confirmLabel: "Voltar para rascunho",
      loadingLabel: "Atualizando…",
      variant: "warning",
      dataTestId: `confirm-draft-evaluation-${evaluation.id}`,
      onConfirm: async () => {
        await api.post(`/evaluations/${evaluation.id}/draft`);
        toast.success("Avaliação voltou para rascunho");
        await load();
      },
      onError: (error) => toast.error(formatApiError(error)),
    });
  };

  const clone = async (evaluation) => {
    try {
      const { data } = await api.post(`/evaluations/${evaluation.id}/clone`);
      toast.success("Cópia criada em rascunho");
      await load();
      setEditing(data.id);
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const closeEvaluation = (evaluation) => confirmAction({
    title: "Encerrar avaliação?",
    description: <>Ao encerrar <strong className="font-semibold text-[#1F1F1F]">{evaluation.title}</strong>, os alunos não poderão iniciar novas tentativas.</>,
    confirmLabel: "Encerrar avaliação",
    loadingLabel: "Encerrando…",
    variant: "warning",
    dataTestId: `confirm-close-evaluation-${evaluation.id}`,
    onConfirm: async () => {
      await api.post(`/evaluations/${evaluation.id}/close`);
      toast.success("Avaliação encerrada");
      await load();
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  const archive = (evaluation) => confirmAction({
    title: "Arquivar avaliação?",
    description: <><strong className="font-semibold text-[#1F1F1F]">{evaluation.title}</strong> sairá das visões ativas e permanecerá preservada no histórico.</>,
    confirmLabel: "Arquivar",
    loadingLabel: "Arquivando…",
    variant: "warning",
    dataTestId: `confirm-archive-evaluation-${evaluation.id}`,
    onConfirm: async () => {
      await api.post(`/evaluations/${evaluation.id}/archive`);
      toast.success("Avaliação arquivada");
      await load();
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 sm:p-6 lg:p-8" data-testid="evaluations-page">
      <PageHeader
        compact
        eyebrow="Acadêmico"
        title="Avaliações"
        description="Crie, publique e acompanhe avaliações sem perder a visão do ciclo de cada prova."
        meta={`${scopedItems.length} avaliação(ões) no filtro atual`}
        action={can("eval.create") ? (
          <button onClick={() => setWizard(true)} className="doxa-btn-primary inline-flex items-center gap-2 px-4 py-2.5" data-testid="new-evaluation-btn">
            <Plus className="h-4 w-4" /> Nova avaliação
          </button>
        ) : null}
      />

      <section className="rounded-2xl border border-[#E0D9CD] bg-white p-3 sm:p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="inline-flex w-full rounded-xl bg-[#F6F3EE] p-1 lg:w-auto" role="group" aria-label="Filtrar avaliações por estado">
            {STATUS_OPTS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setStatus(option.value)}
                className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition lg:flex-none ${status === option.value ? "bg-white text-[#D94A12] shadow-sm" : "text-[#6B655E] hover:text-[#1F1F1F]"}`}
                data-testid={`eval-status-${option.value}`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A59C90]" />
            <input
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder="Buscar por título ou público…"
              className="h-11 w-full rounded-xl border border-[#E0D9CD] bg-white pl-9 pr-9 text-sm outline-none transition placeholder:text-[#AAA198] hover:border-[#CFC5B7] focus:border-[#F75A1B] focus:ring-2 focus:ring-[#F75A1B]/15"
              data-testid="evaluations-search"
            />
            {q && (
              <button type="button" onClick={() => setQ("")} className="absolute right-2.5 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-lg text-[#8A8378] hover:bg-[#FFF0E9] hover:text-[#D94A12]" aria-label="Limpar busca">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </section>

      {loading ? (
        <PageLoading label="Carregando avaliações…" />
      ) : filtered.length === 0 ? (
        <section className="rounded-2xl border border-[#E0D9CD] bg-white">
          <EmptyState
            icon={FileText}
            title={scopedItems.length === 0 ? "Nenhuma avaliação neste filtro" : "Nenhum resultado encontrado"}
            description={scopedItems.length === 0 ? "Quando houver avaliações, elas aparecerão aqui com estado, público e andamento." : "Tente limpar a busca ou usar outro termo."}
          />
        </section>
      ) : (
        <div className="space-y-3">
          {filtered.map((evaluation) => {
            const canEditDraft = evaluation.status === "DRAFT" && can("eval.edit");
            const canViewResults = evaluation.status !== "DRAFT" && can("eval.view_results");
            return (
              <article key={evaluation.id} className="group relative overflow-hidden rounded-2xl border border-[#E0D9CD] bg-white transition hover:border-[#D6C9BA] hover:shadow-[0_10px_28px_rgba(68,53,35,0.05)]" data-testid={`evaluation-row-${evaluation.id}`}>
                <div className="absolute inset-y-0 left-0 w-1 bg-[#F75A1B] opacity-70" />
                <div className="p-4 pl-5 sm:p-5 sm:pl-6">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <StatusChip status={evaluation.status} />
                        {evaluation.needs_grading_count > 0 && (
                          <span className="inline-flex h-7 items-center gap-1.5 rounded-full border border-[#F3D9A7] bg-[#FFF7E8] px-2.5 text-[11px] font-semibold text-[#9A6518]" data-testid={`eval-needs-grading-${evaluation.id}`}>
                            <ListChecks className="h-3.5 w-3.5" /> {evaluation.needs_grading_count} p/ corrigir
                          </span>
                        )}
                        {evaluation.status === "PUBLISHED" && evaluation.has_answers && (
                          <span className="inline-flex h-7 items-center gap-1.5 rounded-full border border-[#E7DFD3] bg-[#F6F3EE] px-2.5 text-[11px] font-medium text-[#7B7369]" data-testid={`eval-edit-locked-${evaluation.id}`}>
                            <Lock className="h-3.5 w-3.5" /> edição bloqueada
                          </span>
                        )}
                      </div>

                      <h2 className="mt-3 font-display text-lg font-bold leading-snug text-[#111111] sm:text-xl">{evaluation.title}</h2>

                      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
                        <MetaItem icon={Users}>{evaluation.audience_label} · {evaluation.target_count} aluno(s)</MetaItem>
                        <MetaItem icon={Timer}>{evaluation.time_limit_minutes > 0 ? `${evaluation.time_limit_minutes} min` : "Sem tempo"}</MetaItem>
                        <MetaItem icon={RotateCcw}>{evaluation.max_attempts} tentativa(s)</MetaItem>
                        <MetaItem icon={BookOpenCheck}>{evaluation.question_count} questão(ões)</MetaItem>
                        <MetaItem icon={CheckCircle2}>{evaluation.submitted_count} enviada(s)</MetaItem>
                      </div>

                      {(evaluation.opens_at || evaluation.closes_at) && (
                        <div className="mt-3 rounded-xl bg-[#FAF7F1] px-3 py-2 text-xs text-[#6B655E]">
                          {evaluation.opens_at && <span>Abre {fmtDateTime(evaluation.opens_at)}</span>}
                          {evaluation.opens_at && evaluation.closes_at && <span className="mx-2 text-[#C8BFB4]">•</span>}
                          {evaluation.closes_at && <span>Encerra {fmtDateTime(evaluation.closes_at)}</span>}
                        </div>
                      )}
                    </div>

                    <div className="flex shrink-0 items-center gap-2 self-end lg:self-start">
                      {canViewResults ? (
                        <button
                          type="button"
                          onClick={() => setResults(evaluation.id)}
                          className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-[#FFF0E9] px-3 text-xs font-semibold text-[#D94A12] transition hover:bg-[#FFE4D8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/25"
                          data-testid={`primary-evaluation-${evaluation.id}`}
                        >
                          <BarChart3 className="h-3.5 w-3.5" /> Resultados
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setEditing(evaluation.id)}
                          className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-[#FFF0E9] px-3 text-xs font-semibold text-[#D94A12] transition hover:bg-[#FFE4D8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/25"
                          data-testid={`edit-evaluation-${evaluation.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" /> {canEditDraft ? "Editar" : "Ver"}
                        </button>
                      )}

                      <EvaluationMenu
                        evaluation={evaluation}
                        can={can}
                        isBusy={isBusy}
                        run={run}
                        onEdit={() => setEditing(evaluation.id)}
                        onResults={() => setResults(evaluation.id)}
                        onPublish={() => publish(evaluation)}
                        onDraft={() => returnToDraft(evaluation)}
                        onClone={() => clone(evaluation)}
                        onCloseEvaluation={() => closeEvaluation(evaluation)}
                        onArchive={() => archive(evaluation)}
                      />
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {wizard && <CreateWizard onClose={() => setWizard(false)} onCreated={(id) => { setWizard(false); load(); setEditing(id); }} />}
      {editing && <EvaluationEditor evaluationId={editing} onClose={() => { setEditing(null); load(); }} />}
      {results && <ResultsModal evaluationId={results} onClose={() => { setResults(null); load(); }} />}
    </div>
  );
}

function AudiencePicker({ form, setForm, classGroups, cells, students }) {
  const toggleStudent = (id) => {
    setForm((current) => ({
      ...current,
      student_ids: current.student_ids.includes(id)
        ? current.student_ids.filter((studentId) => studentId !== id)
        : [...current.student_ids, id],
    }));
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="mb-1 block text-sm font-medium">Público-alvo</label>
        <DoxaSelect
          value={form.audience_type}
          onValueChange={(value) => setForm({ ...form, audience_type: value })}
          options={[
            { value: "CLASS", label: "Turma específica" },
            { value: "CELL", label: "Célula específica" },
            { value: "STUDENTS", label: "Alunos específicos" },
          ]}
          className="w-full"
          testId="eval-audience"
          ariaLabel="Público-alvo da avaliação"
        />
      </div>

      {form.audience_type === "CLASS" && (
        <div>
          <label className="mb-1 block text-sm font-medium">Turma</label>
          <DoxaSelect
            value={form.class_group_id}
            onValueChange={(value) => setForm({ ...form, class_group_id: value })}
            options={[{ value: "", label: "Selecione a turma…" }, ...classGroups.map((item) => ({ value: item.id, label: item.name }))]}
            className="w-full"
            testId="eval-class"
            ariaLabel="Turma da avaliação"
          />
        </div>
      )}

      {form.audience_type === "CELL" && (
        <div>
          <label className="mb-1 block text-sm font-medium">Célula</label>
          <DoxaSelect
            value={form.cell_id}
            onValueChange={(value) => setForm({ ...form, cell_id: value })}
            options={[{ value: "", label: "Selecione a célula…" }, ...cells.map((item) => ({ value: item.id, label: item.name }))]}
            className="w-full"
            testId="eval-cell"
            ariaLabel="Célula da avaliação"
          />
        </div>
      )}

      {form.audience_type === "STUDENTS" && (
        <div className="max-h-52 overflow-y-auto rounded-xl border border-[#E0D9CD] bg-white p-2" data-testid="eval-students">
          {students.map((student) => (
            <label key={student.id} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm transition hover:bg-[#FFF7F2]">
              <input
                type="checkbox"
                checked={form.student_ids.includes(student.id)}
                onChange={() => toggleStudent(student.id)}
                className="accent-[#F75A1B]"
                data-testid={`eval-student-${student.id}`}
              />
              <span className="min-w-0 truncate">{student.full_name}</span>
            </label>
          ))}
          {students.length === 0 && <p className="px-2 py-4 text-center text-xs text-[#6B655E]">Nenhum aluno ativo.</p>}
        </div>
      )}
    </div>
  );
}

function WizardSteps({ step }) {
  const steps = [
    [1, "Tipos"],
    [2, "Configuração"],
    [3, "Público"],
  ];
  return (
    <div className="grid grid-cols-3 gap-2">
      {steps.map(([number, label]) => (
        <div key={number} className={`rounded-xl border px-3 py-2 ${step === number ? "border-[#F75A1B] bg-[#FFF0E9]" : step > number ? "border-[#CFE8D8] bg-[#EEF8F2]" : "border-[#E0D9CD] bg-white"}`}>
          <div className="flex items-center gap-2">
            <span className={`grid h-6 w-6 place-items-center rounded-full text-[11px] font-bold ${step === number ? "bg-[#F75A1B] text-white" : step > number ? "bg-[#2F855A] text-white" : "bg-[#F3EFE7] text-[#8A8378]"}`}>
              {step > number ? "✓" : number}
            </span>
            <span className="hidden text-xs font-semibold text-[#4A4640] sm:inline">{label}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function CreateWizard({ onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [classGroups, setClassGroups] = useState([]);
  const [cells, setCells] = useState([]);
  const [students, setStudents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    instructions: "",
    question_types: [],
    audience_type: "CLASS",
    class_group_id: "",
    cell_id: "",
    student_ids: [],
    opens_at: "",
    closes_at: "",
    time_limit_minutes: 30,
    max_attempts: 1,
    passing_score: 6,
    show_score_to_student: true,
    show_correct_answers_to_student: false,
  });

  useEffect(() => {
    api.get("/class-groups").then(({ data }) => setClassGroups(data));
    api.get("/cells").then(({ data }) => setCells(data));
    api.get("/students").then(({ data }) => setStudents(data));
  }, []);

  const toggleType = (type) => {
    setForm((current) => ({
      ...current,
      question_types: current.question_types.includes(type)
        ? current.question_types.filter((item) => item !== type)
        : [...current.question_types, type],
    }));
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Informe o título.");
    setBusy(true);
    try {
      const payload = {
        ...form,
        class_group_id: form.audience_type === "CLASS" ? form.class_group_id : null,
        cell_id: form.audience_type === "CELL" ? form.cell_id : null,
        student_ids: form.audience_type === "STUDENTS" ? form.student_ids : [],
        opens_at: form.opens_at ? new Date(form.opens_at).toISOString() : null,
        closes_at: form.closes_at ? new Date(form.closes_at).toISOString() : null,
        time_limit_minutes: Number(form.time_limit_minutes) || 0,
        max_attempts: Number(form.max_attempts) || 1,
        passing_score: Number(form.passing_score),
      };
      const { data } = await api.post("/evaluations", payload);
      toast.success("Rascunho criado! Agora adicione as questões.");
      onCreated(data.id);
    } catch (error) { toast.error(formatApiError(error)); } finally { setBusy(false); }
  };

  return (
    <ModalShell onClose={onClose} testId="evaluation-wizard" maxWidth="max-w-2xl">
      <ModalHeader
        eyebrow="Nova avaliação"
        title={`Etapa ${step} de 3`}
        description={step === 1 ? "Defina quais formatos de questão poderão ser usados." : step === 2 ? "Configure conteúdo, janela, tentativas e critérios." : "Escolha quem receberá esta avaliação."}
        onClose={onClose}
      />

      <div className="space-y-5 p-5 sm:p-6">
        <WizardSteps step={step} />

        {step === 1 && (
          <SectionCard title="Tipos de questão" description="Você poderá combinar formatos automáticos e dissertativos." icon={ListChecks} compact>
            <div className="space-y-2" data-testid="eval-type-picker">
              {Object.entries(QTYPE_LABEL).map(([value, label]) => {
                const selected = form.question_types.includes(value);
                return (
                  <label key={value} className={`flex cursor-pointer items-center justify-between gap-3 rounded-xl border p-3 transition ${selected ? "border-[#F75A1B] bg-[#FFF7F2]" : "border-[#E0D9CD] bg-white hover:border-[#D8CFC2]"}`} data-testid={`eval-type-${value}`}>
                    <div className="flex items-center gap-3">
                      <input type="checkbox" checked={selected} onChange={() => toggleType(value)} className="accent-[#F75A1B]" />
                      <div>
                        <p className="text-sm font-semibold">{label}</p>
                        <p className="mt-0.5 text-xs text-[#6B655E]">{value === "ESSAY" ? "Correção manual" : "Correção automática"}</p>
                      </div>
                    </div>
                    {selected && <CheckCircle2 className="h-4 w-4 text-[#F75A1B]" />}
                  </label>
                );
              })}
            </div>
            <button onClick={() => { if (form.question_types.length === 0) return toast.error("Selecione ao menos um tipo."); setStep(2); }} className="doxa-btn-primary mt-4 w-full py-2.5" data-testid="eval-step1-next">Continuar</button>
          </SectionCard>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <SectionCard title="Conteúdo" description="O aluno verá estas informações antes de iniciar." icon={FileText} compact>
              <div className="space-y-3">
                <div><label className="mb-1 block text-sm font-medium">Título</label><input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} className={inputCls} data-testid="eval-title" /></div>
                <div><label className="mb-1 block text-sm font-medium">Descrição</label><textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} rows={2} className={inputCls} data-testid="eval-description" /></div>
                <div><label className="mb-1 block text-sm font-medium">Instruções</label><textarea value={form.instructions} onChange={(event) => setForm({ ...form, instructions: event.target.value })} rows={2} className={inputCls} data-testid="eval-instructions" /></div>
              </div>
            </SectionCard>

            <SectionCard title="Regras da tentativa" description="Janela de disponibilidade, tempo, tentativas e nota mínima." icon={Timer} compact>
              <div className="grid gap-3 sm:grid-cols-2">
                <div><label className="mb-1 block text-xs font-medium text-[#6B655E]">Abre em <span className="font-normal">(opcional)</span></label><input type="datetime-local" value={form.opens_at} onChange={(event) => setForm({ ...form, opens_at: event.target.value })} className={inputCls} data-testid="eval-opens" /></div>
                <div><label className="mb-1 block text-xs font-medium text-[#6B655E]">Encerra em <span className="font-normal">(opcional)</span></label><input type="datetime-local" value={form.closes_at} onChange={(event) => setForm({ ...form, closes_at: event.target.value })} className={inputCls} data-testid="eval-closes" /></div>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <div><label className="mb-1 block text-xs font-medium text-[#6B655E]">Tempo (min)</label><input type="number" min="0" value={form.time_limit_minutes} onChange={(event) => setForm({ ...form, time_limit_minutes: event.target.value })} className={inputCls} data-testid="eval-time" /></div>
                <div><label className="mb-1 block text-xs font-medium text-[#6B655E]">Tentativas</label><input type="number" min="1" value={form.max_attempts} onChange={(event) => setForm({ ...form, max_attempts: event.target.value })} className={inputCls} data-testid="eval-attempts" /></div>
                <div><label className="mb-1 block text-xs font-medium text-[#6B655E]">Nota mín. (0–10)</label><input type="number" min="0" max="10" step="0.5" value={form.passing_score} onChange={(event) => setForm({ ...form, passing_score: event.target.value })} className={inputCls} data-testid="eval-passing" /></div>
              </div>
              <div className="mt-4 space-y-2 rounded-xl bg-[#FAF7F1] p-3">
                <label className="flex cursor-pointer items-center gap-2 text-sm"><input type="checkbox" checked={form.show_score_to_student} onChange={(event) => setForm({ ...form, show_score_to_student: event.target.checked })} className="accent-[#F75A1B]" data-testid="eval-show-score" /> Aluno pode ver a nota</label>
                <label className="flex cursor-pointer items-center gap-2 text-sm"><input type="checkbox" checked={form.show_correct_answers_to_student} onChange={(event) => setForm({ ...form, show_correct_answers_to_student: event.target.checked })} className="accent-[#F75A1B]" data-testid="eval-show-answers" /> Aluno pode ver o gabarito</label>
              </div>
            </SectionCard>

            <div className="flex gap-2">
              <button onClick={() => setStep(1)} className="flex-1 rounded-xl bg-[#F3EFE7] py-2.5 font-semibold text-[#4A4640]" data-testid="eval-step2-back">Voltar</button>
              <button onClick={() => { if (!form.title.trim()) return toast.error("Informe o título."); setStep(3); }} className="doxa-btn-primary flex-1 py-2.5" data-testid="eval-step2-next">Continuar</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <SectionCard title="Público da avaliação" description="Escolha a turma, célula ou alunos específicos que receberão a prova." icon={Target} compact>
            <AudiencePicker form={form} setForm={setForm} classGroups={classGroups} cells={cells} students={students} />
            <div className="mt-4 flex gap-2">
              <button onClick={() => setStep(2)} className="flex-1 rounded-xl bg-[#F3EFE7] py-2.5 font-semibold text-[#4A4640]" data-testid="eval-step3-back">Voltar</button>
              <button onClick={submit} disabled={busy} className="doxa-btn-primary flex-1 py-2.5 disabled:opacity-60" data-testid="eval-create-submit">{busy ? "Criando…" : "Criar rascunho"}</button>
            </div>
          </SectionCard>
        )}
      </div>
    </ModalShell>
  );
}

function EvalGeneralForm({ ev, onSaved }) {
  const [classGroups, setClassGroups] = useState([]);
  const [cells, setCells] = useState([]);
  const [students, setStudents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: ev.title || "",
    description: ev.description || "",
    instructions: ev.instructions || "",
    question_types: ev.question_types || [],
    audience_type: ev.audience_type || "CLASS",
    class_group_id: ev.class_group_id || "",
    cell_id: ev.cell_id || "",
    student_ids: ev.student_ids || [],
    opens_at: toLocalInput(ev.opens_at),
    closes_at: toLocalInput(ev.closes_at),
    time_limit_minutes: ev.time_limit_minutes ?? 0,
    max_attempts: ev.max_attempts ?? 1,
    passing_score: ev.passing_score ?? 6,
    show_score_to_student: !!ev.show_score_to_student,
    show_correct_answers_to_student: !!ev.show_correct_answers_to_student,
  });

  useEffect(() => {
    api.get("/class-groups").then(({ data }) => setClassGroups(data));
    api.get("/cells").then(({ data }) => setCells(data));
    api.get("/students").then(({ data }) => setStudents(data));
  }, []);

  const toggleType = (type) => {
    setForm((current) => ({
      ...current,
      question_types: current.question_types.includes(type)
        ? current.question_types.filter((item) => item !== type)
        : [...current.question_types, type],
    }));
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Informe o título.");
    if (form.question_types.length === 0) return toast.error("Selecione ao menos um tipo de questão.");
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        instructions: form.instructions,
        question_types: form.question_types,
        audience_type: form.audience_type,
        class_group_id: form.audience_type === "CLASS" ? form.class_group_id : null,
        cell_id: form.audience_type === "CELL" ? form.cell_id : null,
        student_ids: form.audience_type === "STUDENTS" ? form.student_ids : [],
        opens_at: form.opens_at ? new Date(form.opens_at).toISOString() : null,
        closes_at: form.closes_at ? new Date(form.closes_at).toISOString() : null,
        time_limit_minutes: Number(form.time_limit_minutes) || 0,
        max_attempts: Number(form.max_attempts) || 1,
        passing_score: Number(form.passing_score),
        show_score_to_student: form.show_score_to_student,
        show_correct_answers_to_student: form.show_correct_answers_to_student,
      };
      await api.put(`/evaluations/${ev.id}`, payload);
      toast.success("Dados gerais salvos.");
      onSaved();
    } catch (error) { toast.error(formatApiError(error)); } finally { setBusy(false); }
  };

  return (
    <SectionCard title="Configuração da avaliação" description="Edite conteúdo, regras e público enquanto a avaliação estiver em rascunho." icon={FilePenLine} compact testId="eval-edit-general-form">
      <div className="space-y-4">
        <div>
          <label className="mb-2 block text-sm font-medium">Tipos de questão</label>
          <div className="flex flex-wrap gap-2" data-testid="edit-type-picker">
            {Object.entries(QTYPE_LABEL).map(([value, label]) => {
              const selected = form.question_types.includes(value);
              return (
                <label key={value} className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm transition ${selected ? "border-[#F75A1B] bg-[#FFF7F2]" : "border-[#E0D9CD] bg-white hover:border-[#D8CFC2]"}`} data-testid={`edit-type-${value}`}>
                  <input type="checkbox" checked={selected} onChange={() => toggleType(value)} className="accent-[#F75A1B]" /> {label}
                </label>
              );
            })}
          </div>
        </div>

        <div><label className="mb-1 block text-sm font-medium">Título</label><input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} className={inputCls} data-testid="edit-title" /></div>
        <div><label className="mb-1 block text-sm font-medium">Descrição</label><textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} rows={2} className={inputCls} data-testid="edit-description" /></div>
        <div><label className="mb-1 block text-sm font-medium">Instruções</label><textarea value={form.instructions} onChange={(event) => setForm({ ...form, instructions: event.target.value })} rows={2} className={inputCls} data-testid="edit-instructions" /></div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div><label className="mb-1 block text-xs font-medium text-[#6B655E]">Abre em <span className="font-normal">(opcional)</span></label><input type="datetime-local" value={form.opens_at} onChange={(event) => setForm({ ...form, opens_at: event.target.value })} className={inputCls} data-testid="edit-opens" /></div>
          <div><label className="mb-1 block text-xs font-medium text-[#6B655E]">Encerra em <span className="font-normal">(opcional)</span></label><input type="datetime-local" value={form.closes_at} onChange={(event) => setForm({ ...form, closes_at: event.target.value })} className={inputCls} data-testid="edit-closes" /></div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div><label className="mb-1 block text-xs font-medium text-[#6B655E]">Tempo (min)</label><input type="number" min="0" value={form.time_limit_minutes} onChange={(event) => setForm({ ...form, time_limit_minutes: event.target.value })} className={inputCls} data-testid="edit-time" /></div>
          <div><label className="mb-1 block text-xs font-medium text-[#6B655E]">Tentativas</label><input type="number" min="1" value={form.max_attempts} onChange={(event) => setForm({ ...form, max_attempts: event.target.value })} className={inputCls} data-testid="edit-attempts" /></div>
          <div><label className="mb-1 block text-xs font-medium text-[#6B655E]">Nota mín. (0–10)</label><input type="number" min="0" max="10" step="0.5" value={form.passing_score} onChange={(event) => setForm({ ...form, passing_score: event.target.value })} className={inputCls} data-testid="edit-passing" /></div>
        </div>

        <div className="space-y-2 rounded-xl bg-[#FAF7F1] p-3">
          <label className="flex cursor-pointer items-center gap-2 text-sm"><input type="checkbox" checked={form.show_score_to_student} onChange={(event) => setForm({ ...form, show_score_to_student: event.target.checked })} className="accent-[#F75A1B]" data-testid="edit-show-score" /> Aluno pode ver a nota</label>
          <label className="flex cursor-pointer items-center gap-2 text-sm"><input type="checkbox" checked={form.show_correct_answers_to_student} onChange={(event) => setForm({ ...form, show_correct_answers_to_student: event.target.checked })} className="accent-[#F75A1B]" data-testid="edit-show-answers" /> Aluno pode ver o gabarito</label>
        </div>

        <div className="border-t border-[#EBE5DB] pt-4">
          <AudiencePicker form={form} setForm={setForm} classGroups={classGroups} cells={cells} students={students} />
        </div>

        <button onClick={save} disabled={busy} className="doxa-btn-primary w-full py-2.5 inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="editor-save-general">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {busy ? "Salvando…" : "Salvar dados gerais"}
        </button>
      </div>
    </SectionCard>
  );
}

function EvaluationEditor({ evaluationId, onClose }) {
  const confirmAction = useDoxaConfirm();
  const { can } = usePermissions();
  const [ev, setEv] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);

  const load = () => api.get(`/evaluations/${evaluationId}`).then(({ data }) => setEv(data)).catch((error) => toast.error(formatApiError(error)));
  useEffect(() => { void load(); }, [evaluationId]);

  const isDraft = ev?.status === "DRAFT";
  const editable = !!(isDraft && can("eval.edit"));

  const removeQuestion = (questionId) => confirmAction({
    title: "Remover questão?",
    description: "A questão será removida desta avaliação. Esta é uma ação destrutiva sobre o item selecionado.",
    confirmLabel: "Remover questão",
    loadingLabel: "Removendo…",
    variant: "destructive",
    dataTestId: `confirm-remove-question-${questionId}`,
    onConfirm: async () => {
      await api.delete(`/evaluations/${evaluationId}/questions/${questionId}`);
      await load();
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  const publish = async () => {
    try {
      await api.post(`/evaluations/${evaluationId}/publish`);
      toast.success("Avaliação publicada!");
      onClose();
    } catch (error) { toast.error(formatApiError(error)); }
  };

  if (!ev) return null;

  const subtitle = ev.status === "DRAFT"
    ? (editable ? "Rascunho — edite os dados e as questões antes de publicar." : "Rascunho — visualização sem permissão de edição.")
    : ev.status === "PUBLISHED"
      ? (ev.has_answers ? "Publicada — edição bloqueada porque já existe uma tentativa iniciada." : "Publicada — para editar, volte para rascunho na lista de avaliações.")
      : ev.status === "CLOSED"
        ? "Encerrada — histórico preservado. Para reutilizar, clone a avaliação."
        : "Arquivada — histórico preservado. Para reutilizar, clone a avaliação.";

  return (
    <ModalShell onClose={onClose} testId="evaluation-editor" maxWidth="max-w-3xl">
      <ModalHeader eyebrow="Editor da avaliação" title={ev.title} description={subtitle} onClose={onClose} trailing={<StatusChip status={ev.status} />} />

      <div className="space-y-4 p-5 sm:p-6">
        {editable && <EvalGeneralForm ev={ev} onSaved={load} />}

        <SectionCard
          title="Questões"
          description={`${(ev.questions || []).length} questão(ões) cadastrada(s).`}
          icon={BookOpenCheck}
          compact
          action={editable ? (
            <div className="hidden flex-wrap gap-1.5 sm:flex">
              {ev.question_types.map((type) => (
                <button key={type} type="button" onClick={() => setAdding(type)} className="inline-flex h-8 items-center gap-1 rounded-lg bg-[#FFF0E9] px-2.5 text-[11px] font-semibold text-[#D94A12]" data-testid={`add-question-${type}`}>
                  <Plus className="h-3.5 w-3.5" /> {QTYPE_LABEL[type]}
                </button>
              ))}
            </div>
          ) : null}
        >
          <div className="space-y-3">
            {(ev.questions || []).map((question, index) => (
              <article key={question.id} className="rounded-xl border border-[#E0D9CD] bg-white p-4" data-testid={`editor-question-${question.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-[11px] font-medium text-[#8A8378]">
                      <span className="grid h-6 w-6 place-items-center rounded-full bg-[#FFF0E9] font-bold text-[#D94A12]">{index + 1}</span>
                      <span>{QTYPE_LABEL[question.type]}</span>
                      <span>•</span>
                      <span>{question.points} ponto(s)</span>
                    </div>
                    <p className="mt-2 break-words text-sm font-semibold leading-relaxed text-[#1F1F1F]">{question.prompt}</p>
                    {question.type === "MULTIPLE_CHOICE" && (
                      <ul className="mt-3 space-y-1.5">
                        {question.options.map((option) => (
                          <li key={option.id} className={`rounded-lg px-2.5 py-1.5 text-xs ${option.is_correct ? "bg-[#EEF8F2] font-semibold text-[#2F7A45]" : "bg-[#FAF7F1] text-[#6B655E]"}`}>
                            {option.is_correct ? "✓ " : "• "}{option.text}
                          </li>
                        ))}
                      </ul>
                    )}
                    {question.type === "TRUE_FALSE" && <p className="mt-2 text-xs font-semibold text-[#2F855A]">Correta: {question.correct_boolean ? "Verdadeiro" : "Falso"}</p>}
                    {question.type === "ESSAY" && <p className="mt-2 text-xs text-[#8A8378]">Resposta dissertativa · correção manual</p>}
                  </div>

                  {editable && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[#8A8378] hover:bg-[#FFF0E9] hover:text-[#D94A12]" aria-label={`Ações da questão ${index + 1}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48 rounded-xl border-[#E0D9CD] bg-white p-1.5 shadow-[0_14px_40px_rgba(45,31,20,.14)]">
                        <DropdownMenuItem onSelect={() => setEditingQuestion(question)} className="cursor-pointer rounded-lg px-2.5 py-2.5 focus:bg-[#FFF0E9] focus:text-[#D94A12]" data-testid={`edit-question-${question.id}`}><Pencil className="h-4 w-4" /> Editar questão</DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-[#EBE5DB]" />
                        <DropdownMenuItem onSelect={() => removeQuestion(question.id)} className="cursor-pointer rounded-lg px-2.5 py-2.5 text-[#C53030] focus:bg-[#FFF1F1] focus:text-[#C53030]" data-testid={`remove-question-${question.id}`}><Trash2 className="h-4 w-4" /> Remover questão</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </article>
            ))}

            {(ev.questions || []).length === 0 && <EmptyState icon={BookOpenCheck} title="Nenhuma questão ainda" description={editable ? "Adicione o primeiro item usando um dos tipos permitidos." : "Esta avaliação ainda não possui questões cadastradas."} compact />}
          </div>

          {editable && (
            <div className="mt-4 flex flex-wrap gap-2 sm:hidden">
              {ev.question_types.map((type) => (
                <button key={type} type="button" onClick={() => setAdding(type)} className="inline-flex items-center gap-1 rounded-xl bg-[#FFF0E9] px-3 py-2 text-xs font-semibold text-[#D94A12]" data-testid={`add-question-mobile-${type}`}>
                  <Plus className="h-4 w-4" /> {QTYPE_LABEL[type]}
                </button>
              ))}
            </div>
          )}
        </SectionCard>

        {isDraft && can("eval.lifecycle") && (
          <button onClick={publish} className="doxa-btn-primary w-full py-2.5 inline-flex items-center justify-center gap-2" data-testid="editor-publish-btn"><Send className="h-4 w-4" /> Publicar avaliação</button>
        )}
      </div>

      {adding && <QuestionModal type={adding} evaluationId={evaluationId} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
      {editingQuestion && <QuestionModal type={editingQuestion.type} evaluationId={evaluationId} question={editingQuestion} onClose={() => setEditingQuestion(null)} onSaved={() => { setEditingQuestion(null); load(); }} />}
    </ModalShell>
  );
}

function QuestionModal({ type, evaluationId, question = null, onClose, onSaved }) {
  const [prompt, setPrompt] = useState(question?.prompt || "");
  const [points, setPoints] = useState(question?.points ?? 1);
  const [options, setOptions] = useState(question?.options?.length ? question.options.map((option) => ({ ...option })) : [{ text: "", is_correct: true }, { text: "", is_correct: false }]);
  const [correctBool, setCorrectBool] = useState(question?.correct_boolean ?? true);
  const [busy, setBusy] = useState(false);

  const setCorrect = (index) => setOptions((current) => current.map((option, optionIndex) => ({ ...option, is_correct: optionIndex === index })));

  const save = async () => {
    if (!prompt.trim()) return toast.error("Informe o enunciado.");
    setBusy(true);
    try {
      const payload = { type, prompt, points: Number(points) };
      if (type === "MULTIPLE_CHOICE") payload.options = options.map((option, index) => ({ id: option.id || undefined, text: option.text, is_correct: option.is_correct, order: index + 1 }));
      if (type === "TRUE_FALSE") payload.correct_boolean = correctBool;

      if (question) {
        await api.put(`/evaluations/${evaluationId}/questions/${question.id}`, payload);
        toast.success("Questão atualizada");
      } else {
        await api.post(`/evaluations/${evaluationId}/questions`, payload);
        toast.success("Questão adicionada");
      }
      onSaved();
    } catch (error) { toast.error(formatApiError(error)); } finally { setBusy(false); }
  };

  return (
    <ModalShell onClose={onClose} testId="question-modal" maxWidth="max-w-lg">
      <ModalHeader
        eyebrow="Banco de questões"
        title={`${question ? "Editar" : "Adicionar"} · ${QTYPE_LABEL[type]}`}
        description="Configure o enunciado, a pontuação e a resposta correta quando aplicável."
        onClose={onClose}
      />
      <div className="space-y-4 p-5 sm:p-6">
        <div><label className="mb-1 block text-sm font-medium">Enunciado</label><textarea value={prompt} onChange={(event) => setPrompt(event.target.value)} rows={3} className={inputCls} data-testid="question-prompt" /></div>
        <div><label className="mb-1 block text-sm font-medium">Pontos</label><input type="number" min="0.5" step="0.5" value={points} onChange={(event) => setPoints(event.target.value)} className={inputCls} data-testid="question-points" /></div>

        {type === "MULTIPLE_CHOICE" && (
          <SectionCard title="Alternativas" description="Marque a bolinha correspondente à alternativa correta." icon={ListChecks} compact testId="question-options">
            <div className="space-y-2">
              {options.map((option, index) => (
                <div key={option.id || index} className={`flex items-center gap-2 rounded-xl border p-2 transition ${option.is_correct ? "border-[#CFE8D8] bg-[#F4FBF6]" : "border-[#E0D9CD] bg-white"}`}>
                  <input type="radio" name="correct" checked={option.is_correct} onChange={() => setCorrect(index)} className="accent-[#F75A1B]" data-testid={`option-correct-${index}`} />
                  <input value={option.text} onChange={(event) => setOptions((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, text: event.target.value } : item))} placeholder={`Alternativa ${index + 1}`} className={`${inputCls} border-0 bg-transparent focus:ring-0`} data-testid={`option-text-${index}`} />
                  {options.length > 2 && <button type="button" onClick={() => setOptions((current) => current.filter((_, itemIndex) => itemIndex !== index))} className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[#C53030] hover:bg-[#FFF1F1]" aria-label={`Remover alternativa ${index + 1}`}><X className="h-4 w-4" /></button>}
                </div>
              ))}
            </div>
            <button type="button" onClick={() => setOptions((current) => [...current, { text: "", is_correct: false }])} className="mt-3 inline-flex items-center gap-1 rounded-lg bg-[#FFF0E9] px-3 py-2 text-xs font-semibold text-[#D94A12]" data-testid="add-option-btn"><Plus className="h-3.5 w-3.5" /> Adicionar alternativa</button>
          </SectionCard>
        )}

        {type === "TRUE_FALSE" && (
          <div>
            <label className="mb-2 block text-sm font-medium">Resposta correta</label>
            <div className="grid grid-cols-2 gap-2" data-testid="tf-correct">
              {[["true", "Verdadeiro", true], ["false", "Falso", false]].map(([key, label, value]) => (
                <button key={key} type="button" onClick={() => setCorrectBool(value)} className={`rounded-xl border py-2.5 text-sm font-semibold transition ${correctBool === value ? "border-[#F75A1B] bg-[#F75A1B] text-white" : "border-[#E0D9CD] bg-white text-[#4A4640] hover:border-[#D8CFC2]"}`} data-testid={`tf-${key}`}>{label}</button>
              ))}
            </div>
          </div>
        )}

        {type === "ESSAY" && (
          <div className="rounded-xl border border-[#E0D9CD] bg-[#FAF7F1] p-3 text-xs leading-relaxed text-[#6B655E]">
            Questões dissertativas serão enviadas para a fila de Correções após a submissão do aluno.
          </div>
        )}

        <button onClick={save} disabled={busy} className="doxa-btn-primary w-full py-2.5 disabled:opacity-60" data-testid="question-save">{busy ? "Salvando…" : question ? "Salvar alterações" : "Adicionar questão"}</button>
      </div>
    </ModalShell>
  );
}

function ResultStatusChip({ status }) {
  const labels = {
    NOT_STARTED: "Não fez",
    IN_PROGRESS: "Em andamento",
    NEEDS_GRADING: "Aguardando correção",
    GRADED: "Corrigida",
    SUBMITTED: "Enviada",
    AUTO_SUBMITTED: "Enviada (tempo)",
    EXPIRED: "Expirada",
  };
  const tone = status === "GRADED" ? "border-[#CFE8D8] bg-[#EEF8F2] text-[#2F7A45]" : status === "NEEDS_GRADING" ? "border-[#F3D9A7] bg-[#FFF7E8] text-[#9A6518]" : status === "IN_PROGRESS" ? "border-[#FFD8C8] bg-[#FFF0E9] text-[#D94A12]" : "border-[#E7DFD3] bg-[#F6F3EE] text-[#6B655E]";
  return <span className={`inline-flex rounded-full border px-2 py-1 text-[10px] font-semibold ${tone}`}>{labels[status] || status}</span>;
}

function ResultsModal({ evaluationId, onClose }) {
  const [data, setData] = useState(null);
  const [extraFor, setExtraFor] = useState(null);
  const { isBusy: extraBusy, run: extraRun } = useBusyKeys();

  const load = () => api.get(`/evaluations/${evaluationId}/results`).then(({ data: response }) => setData(response)).catch((error) => toast.error(formatApiError(error)));
  useEffect(() => { void load(); }, [evaluationId]);
  useVisibleRefresh(() => load(), { intervalMs: 0 });

  const grantExtra = async (studentId) => {
    try {
      await api.post(`/evaluations/${evaluationId}/extra-attempt`, { student_id: studentId, attempts_added: 1 });
      toast.success("Tentativa extra liberada");
      setExtraFor(null);
      await load();
    } catch (error) { toast.error(formatApiError(error)); }
  };

  if (!data) return null;
  const totals = data.totals;
  const metrics = [
    ["Alvo", totals.target, Users],
    ["Enviaram", totals.submitted, CheckCircle2],
    ["Não fizeram", totals.not_done, Target],
    ["Aguard. correção", totals.needs_grading, ListChecks],
    ["Aprovados", totals.approved, CircleCheck],
    ["Reprovados", totals.reproved, X],
    ["Média", totals.avg_score ?? "—", BarChart3],
  ];

  return (
    <ModalShell onClose={onClose} testId="results-modal" maxWidth="max-w-3xl">
      <ModalHeader eyebrow="Resultados" title={data.evaluation.title} description="Acompanhe participação, notas e tentativas dos alunos." onClose={onClose} />

      <div className="space-y-4 p-5 sm:p-6">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {metrics.map(([label, value, Icon]) => (
            <div key={label} className="rounded-xl border border-[#E0D9CD] bg-white p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-medium text-[#6B655E]">{label}</p>
                <Icon className="h-3.5 w-3.5 text-[#F75A1B]" />
              </div>
              <p className="mt-1 font-display text-xl font-bold">{value}</p>
            </div>
          ))}
        </div>

        <a href={`/evaluations/${evaluationId}/analytics`} className="doxa-btn-primary inline-flex w-full items-center justify-center gap-2 py-2.5" data-testid="open-evaluation-analytics"><BarChart3 className="h-4 w-4" /> Abrir análise completa</a>

        <SectionCard title="Alunos" description="Estado atual, uso de tentativas e resultado final." icon={Users} compact>
          <div className="divide-y divide-[#EBE5DB]" data-testid="results-rows">
            {data.rows.map((row) => (
              <div key={row.student_id} className="flex flex-col gap-3 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between" data-testid={`result-row-${row.student_id}`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="break-words text-sm font-semibold">{row.full_name}</p>
                    <ResultStatusChip status={row.status} />
                    {row.passed === true && <span className="text-[10px] font-semibold text-[#2F7A45]">Aprovado</span>}
                    {row.passed === false && <span className="text-[10px] font-semibold text-[#C53030]">Reprovado</span>}
                  </div>
                  <p className="mt-1 text-xs text-[#6B655E]">{row.attempts_used}/{row.attempts_allowed} tentativa(s){row.final_score != null ? ` · Nota ${row.final_score}` : ""}</p>
                </div>
                <button onClick={() => setExtraFor(row.student_id)} className="inline-flex h-8 shrink-0 items-center justify-center gap-1 rounded-lg bg-[#FFF0E9] px-3 text-xs font-semibold text-[#D94A12] hover:bg-[#FFE4D8]" data-testid={`extra-attempt-${row.student_id}`}><Plus className="h-3.5 w-3.5" /> Tentativa</button>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      {extraFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setExtraFor(null)}>
          <div className="w-full max-w-sm rounded-2xl border border-[#E0D9CD] bg-white p-5 shadow-[0_20px_60px_rgba(45,31,20,.22)]" onClick={(event) => event.stopPropagation()} data-testid="extra-attempt-modal">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#FFF0E9] text-[#F75A1B]"><RotateCcw className="h-5 w-5" /></span>
            <h3 className="mt-3 font-display text-lg font-bold">Liberar tentativa extra?</h3>
            <p className="mt-1 text-sm leading-relaxed text-[#6B655E]">Será adicionada 1 tentativa para este aluno, preservando o histórico já existente.</p>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setExtraFor(null)} className="flex-1 rounded-xl bg-[#F3EFE7] py-2.5 font-semibold text-[#4A4640]">Cancelar</button>
              <button disabled={extraBusy(`extra:${extraFor}`)} onClick={() => extraRun(`extra:${extraFor}`, () => grantExtra(extraFor))} className="doxa-btn-primary flex-1 py-2.5 disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="confirm-extra-attempt">{extraBusy(`extra:${extraFor}`) ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Liberar</button>
            </div>
          </div>
        </div>
      )}
    </ModalShell>
  );
}
